import "./LLMSidePanel.scss";
import cn from "classnames";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import {
  MdArrowBack,
  MdClose,
  MdEdit,
  MdInventory,
  MdLink,
  MdOutlineHistory,
  MdOutlineInventory2,
  MdOutlinePushPin,
  MdSearch
} from "react-icons/md";
import { LLMSession, SessionId, UpdateSessionArgs } from "@blisspointmedia/bpm-types/dist/LLM";
import { Button, ButtonType } from "../../Components/Button/Button";
import { ButtonFrameworkVariant } from "../../Components/ButtonFramework/ButtonFramework";
import { Spinner } from "../../Components/Spinner";
import {
  SidePanelDivider,
  SidePanelListItem,
  SidePanelListItemAction,
  SidePanelSectionHeader
} from "../../Components/FloatingPanel/SidePanel";
import { useSetError } from "../../redux/modals";
import { Brand50 } from "../../utils/colors";
import { MiscLambdaFetch } from "../../utils/fetch-utils";
import { Mixpanel, MxE } from "../../utils/mixpanelWrapper";
import { StateSetter } from "../../utils/types";
import { PRIVACY_POLICY_URL } from "../LLMConstants";
import { ReactComponent as NewChat } from "../NewChat.svg";

interface SessionListAction {
  icon: JSX.Element;
  label: string;
  action: (sessionId: string) => void;
}

interface SessionSectionProps {
  sessionList: LLMSession[];
  currentSessionId: string | undefined;
  sessionActions: SessionListAction[];
  visibleSessionActionsMenu: SessionId | undefined;
  setVisibleSessionActionsMenu: StateSetter<SessionId | undefined>;
  selectSession: (sessionId: any) => void;
  emptyMessage: string;
  submittingSessions: Set<string>;
  renamingSessions: Set<string>;
  renameInputs: Record<string, string>;
  setRenameInput: Dispatch<SetStateAction<Record<string, string>>>;
  updateSessionState: ({ sessionId, newState, newName }: UpdateSessionArgs) => Promise<void>;
  cancelRename: (sessionId: any) => void;
}

const SessionSection = ({
  sessionList,
  currentSessionId,
  sessionActions,
  visibleSessionActionsMenu,
  setVisibleSessionActionsMenu,
  selectSession,
  emptyMessage,
  submittingSessions,
  renamingSessions,
  renameInputs,
  setRenameInput,
  updateSessionState,
  cancelRename,
}: SessionSectionProps): JSX.Element => (
  <div className="sessionList">
    {sessionList.map(({ sessionId, timestamp, sessionName }) => {
      const actions: SidePanelListItemAction[] = sessionActions.map(({ icon, label, action }) => ({
        icon,
        label,
        onClick: () => {
          action(sessionId);
        },
      }));
      const submitting = submittingSessions.has(sessionId);
      const renaming = renamingSessions.has(sessionId);
      const newName = renameInputs[sessionId] ?? "";
      const isNewNameValid = newName.length > 0 && newName.length <= 32;

      const actionsIconReplacement = submitting ? (
        <Spinner size={18} />
      ) : renaming ? (
        <>
          <Button
            type={ButtonType.EMPTY}
            variant={ButtonFrameworkVariant.ICON_ONLY}
            design="secondary"
            size="sm"
            className="submitSessionRename"
            icon={<FaCheck size={12} />}
            onClick={() => updateSessionState({ sessionId, newName })}
            disabled={!isNewNameValid}
          />
          <Button
            type={ButtonType.EMPTY}
            variant={ButtonFrameworkVariant.ICON_ONLY}
            design="secondary"
            size="sm"
            className="cancelSessionRename"
            icon={<MdClose size={18} />}
            onClick={() => cancelRename(sessionId)}
          />
        </>
      ) : undefined;

      const renameInput = (
        <Form.Group controlId={`renameSession_${sessionId}`}>
          <Form.Control
            value={newName}
            onChange={e => setRenameInput({ ...renameInputs, [sessionId]: e.target?.value })}
            onKeyDown={e => {
              if (e.key === "Enter" && isNewNameValid) {
                updateSessionState({ sessionId, newName });
              } else if (e.key === "Escape") {
                cancelRename(sessionId);
              }
            }}
            disabled={submitting}
            autoFocus
          />
        </Form.Group>
      );

      return (
        <SidePanelListItem
          key={timestamp}
          text={renaming ? renameInput : sessionName}
          actions={actions}
          showActionsMenu={visibleSessionActionsMenu === sessionId}
          setShowActionsMenu={(show: boolean) => {
            show
              ? setVisibleSessionActionsMenu(sessionId)
              : setVisibleSessionActionsMenu(undefined);
          }}
          passthroughProps={{
            onClick:
              submitting || renaming || currentSessionId === sessionId
                ? undefined
                : () => selectSession(sessionId),
            className: cn("listItem", {
              active: currentSessionId === sessionId,
              renaming,
              submitting,
            }),
          }}
          actionsIconReplacement={actionsIconReplacement}
        />
      );
    })}
    {sessionList.length === 0 && <div className="empty">{emptyMessage}</div>}
  </div>
);

interface LLMSidePanelBodyProps {
  sessions: Record<SessionId, LLMSession> | undefined;
  setSessions: React.Dispatch<React.SetStateAction<Record<string, LLMSession> | undefined>>;
  currentSessionId: string | undefined;
  selectSession: (sessionId: any) => void;
  startNewSession: () => void;
}

const LLMSidePanelBody = ({
  sessions,
  setSessions,
  currentSessionId,
  selectSession,
  startNewSession,
}: LLMSidePanelBodyProps): JSX.Element => {
  const [visibleSessionActionsMenu, setVisibleSessionActionsMenu] = useState<
    SessionId | undefined
  >();
  const [showArchive, setShowArchive] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchArchiveInput, setSearchArchiveInput] = useState("");
  const [renameInputs, setRenameInput] = useState<Record<SessionId, string>>({});
  const [submittingSessions, setSubmittingSessions] = useState(new Set<string>());
  const [renamingSessions, setRenamingSessions] = useState(new Set<string>());
  const setError = useSetError();

  const clearRenameState = useCallback(sessionId => {
    setRenameInput(prev => {
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
    setRenamingSessions(prev => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  }, []);

  const clearSubmitState = useCallback(sessionId => {
    setSubmittingSessions(prev => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  }, []);

  const updateSessionState = useCallback(
    async ({ sessionId, newState, newName }: UpdateSessionArgs) => {
      if (submittingSessions.has(sessionId)) {
        console.error("Cannot updateSessionState while already submitting an update.");
        return;
      }

      if (!sessions) {
        return;
      }

      try {
        setSubmittingSessions(prev => new Set(prev).add(sessionId));

        const body: UpdateSessionArgs = {
          sessionId,
          newState,
          newName,
        };

        await MiscLambdaFetch("/updateLLMSession", {
          method: "POST",
          body,
          retries: 0,
        });

        if (newState !== undefined) {
          sessions[sessionId].state = newState;
        }
        if (newName !== undefined) {
          sessions[sessionId].sessionName = newName;
        }
        setSessions({ ...sessions });
        clearRenameState(sessionId);
      } catch (e: any) {
        setError({ message: e.message });
      } finally {
        clearSubmitState(sessionId);
      }
    },
    [clearRenameState, clearSubmitState, sessions, setError, setSessions, submittingSessions]
  );

  const defaultActions: SessionListAction[] = useMemo(
    () => [
      {
        icon: <MdOutlinePushPin color={Brand50} />,
        label: "Pin",
        action: sessionId => {
          Mixpanel.track(MxE.LLM_SESSION_STATE_MANAGEMENT, { type: "Pin" });
          updateSessionState({ sessionId, newState: "PINNED" });
        },
      },
      {
        icon: <MdEdit color={Brand50} />,
        label: "Rename",
        action: sessionId => {
          if (!sessions || !sessions[sessionId]) {
            return;
          }

          Mixpanel.track(MxE.LLM_SESSION_STATE_MANAGEMENT, { type: "Rename" });
          setRenamingSessions(prev => new Set(prev).add(sessionId));
          setRenameInput(prev => ({ ...prev, [sessionId]: sessions[sessionId].sessionName }));
        },
      },
      // {
      //   label: "Share",
      //   action: () => setError({ message: "TODO: Implement share" }),
      // },
      // {
      //   label: "Delete",
      //   action: () => setError({ message: "TODO: Implement delete" }),
      // },
      {
        icon: <MdInventory color={Brand50} />,
        label: "Archive",
        action: sessionId => {
          Mixpanel.track(MxE.LLM_SESSION_STATE_MANAGEMENT, { type: "Archive" });
          updateSessionState({ sessionId, newState: "ARCHIVED" });
        },
      },
    ],
    [sessions, updateSessionState]
  );

  const pinnedActions: SessionListAction[] = useMemo(
    () => [
      {
        icon: <MdOutlinePushPin color={Brand50} />,
        label: "Unpin",
        action: sessionId => {
          Mixpanel.track(MxE.LLM_SESSION_STATE_MANAGEMENT, { type: "Unpin" });
          updateSessionState({ sessionId, newState: null });
        },
      },
      ...defaultActions.slice(1, 5),
    ],
    [defaultActions, updateSessionState]
  );

  const archivedActions: SessionListAction[] = useMemo(
    () => [
      {
        icon: <MdInventory color={Brand50} />,
        label: "Unarchive",
        action: sessionId => {
          Mixpanel.track(MxE.LLM_SESSION_STATE_MANAGEMENT, { type: "Unarchive" });
          updateSessionState({ sessionId, newState: null });
        },
      },
      ...defaultActions.slice(1, 5),
    ],
    [defaultActions, updateSessionState]
  );

  const sessionsArr = useMemo(() => Object.values(sessions ?? {}), [sessions]);

  const [filteredPinnedSessions, emptyPinnedMessage] = useMemo(() => {
    const allPinnedSessions = sessionsArr.filter(session => session.state === "PINNED");
    return [
      allPinnedSessions.filter(session =>
        session.sessionName.toLowerCase().includes(searchInput.toLowerCase())
      ),
      allPinnedSessions.length > 0 ? "No matching results" : "No pinned chats yet",
    ];
  }, [searchInput, sessionsArr]);

  const [filteredHistoricalSessions, emptyHistoricalMessage] = useMemo(() => {
    const allHistoricalSessions = sessionsArr.filter(session => session.state === null);
    const hasHistoricalSessions = allHistoricalSessions.length > 0;
    const filteredHistoricalSessions = allHistoricalSessions.filter(session =>
      session.sessionName.toLowerCase().includes(searchInput.toLowerCase())
    );
    let emptyMessage = "";
    if (hasHistoricalSessions && filteredHistoricalSessions.length === 0) {
      // Sessions exist in user's history, but don't match search
      emptyMessage = "No matching results";
    } else if (!hasHistoricalSessions && sessionsArr.length === 0) {
      // No sessions exist in user's history
      emptyMessage = "No chat history yet";
    } else if (!hasHistoricalSessions) {
      // Sessions exist in user's history, but all pinned / archived
      emptyMessage = "Nothing to show here";
    }
    return [filteredHistoricalSessions, emptyMessage];
  }, [searchInput, sessionsArr]);

  const [hasArchivedSessions, filteredArchiveSessions, emptyArchiveMessage] = useMemo(() => {
    const allArchiveSessions = sessionsArr.filter(session => session.state === "ARCHIVED");
    const hasArchivedSessions = allArchiveSessions.length > 0;
    return [
      hasArchivedSessions,
      allArchiveSessions.filter(session =>
        session.sessionName.toLowerCase().includes(searchArchiveInput.toLowerCase())
      ),
      hasArchivedSessions ? "No matching results" : "No archived chats yet",
    ];
  }, [searchArchiveInput, sessionsArr]);

  const sessionSectionDefaults: Omit<
    SessionSectionProps,
    "sessionList" | "sessionActions" | "emptyMessage"
  > = {
    currentSessionId,
    visibleSessionActionsMenu,
    setVisibleSessionActionsMenu,
    selectSession,
    submittingSessions,
    renamingSessions,
    renameInputs,
    setRenameInput,
    updateSessionState,
    cancelRename: clearRenameState,
  };

  if (!sessions) {
    return <Spinner />;
  }

  if (showArchive) {
    return (
      <div className="llmSessions">
        <div className="topSection">
          <SidePanelSectionHeader
            icon={
              <>
                <MdArrowBack />
                <MdOutlineInventory2 />
              </>
            }
            text="Archived"
            onClick={() => setShowArchive(false)}
            className="archiveBack"
          />
          {hasArchivedSessions && (
            <Form.Group controlId="archiveSearch" className="chatSearch">
              <Form.Label>
                <MdSearch />
              </Form.Label>
              <Form.Control
                placeholder="Search Archive"
                value={searchArchiveInput}
                onChange={e => setSearchArchiveInput(e.target?.value)}
              />
            </Form.Group>
          )}
        </div>
        {hasArchivedSessions && <SidePanelDivider />}
        <SessionSection
          sessionList={filteredArchiveSessions}
          sessionActions={archivedActions}
          emptyMessage={emptyArchiveMessage}
          {...sessionSectionDefaults}
        />
      </div>
    );
  }

  return (
    <div className="llmSessions">
      <div className="topSection">
        <Form.Group controlId="chatSearch" className="chatSearch">
          <Form.Label>
            <MdSearch />
          </Form.Label>
          <Form.Control
            placeholder="Search Chats"
            value={searchInput}
            onChange={e => setSearchInput(e.target?.value)}
          />
        </Form.Group>
        <SidePanelSectionHeader icon={<NewChat />} text="New Chat" onClick={startNewSession} />
      </div>
      <SidePanelDivider />
      <SidePanelSectionHeader icon={<MdOutlinePushPin />} text="Pinned" />
      <SessionSection
        sessionList={filteredPinnedSessions}
        sessionActions={pinnedActions}
        emptyMessage={emptyPinnedMessage}
        {...sessionSectionDefaults}
      />
      <SidePanelDivider />
      <SidePanelSectionHeader icon={<MdOutlineHistory />} text="History" />
      <SessionSection
        sessionList={filteredHistoricalSessions}
        sessionActions={defaultActions}
        emptyMessage={emptyHistoricalMessage}
        {...sessionSectionDefaults}
      />
      <SidePanelDivider />
      <SidePanelSectionHeader
        icon={<MdOutlineInventory2 />}
        text="Archived"
        onClick={() => setShowArchive(true)}
      />
    </div>
  );
};

const LLMSidePanelFooter = (): JSX.Element => {
  return (
    <Button
      type={ButtonType.EMPTY}
      variant={ButtonFrameworkVariant.LEADING_ICON}
      size="sm"
      design="secondary"
      icon={<MdLink />}
      onClick={() => window.open(PRIVACY_POLICY_URL, "_blank")}
    >
      Privacy Policy
    </Button>
  );
};

const LLMSidePanel = {
  Body: LLMSidePanelBody,
  Footer: LLMSidePanelFooter,
};

export default LLMSidePanel;
